import Swal from "sweetalert2";
import { findTeuService } from "@/services/findTeuService";
import { getRuleOnSaveMode } from "@/utils/findTeuAutoTrackingSources";

function escapeHtml(text) {
  return String(text == null ? "" : text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Modo «Preguntar al guardar» del mapeo FindTeu: tras un guardado explícito (alta o botón Guardar,
 * nunca el autoguardado), si el ítem tiene número de seguimiento y no tiene un tracking activo,
 * pregunta si se quiere activar. Nunca rompe el guardado: cualquier error se traga.
 *
 * @param {{ modelName: string, itemId: string, t: (key: string, params?: object) => string }} opts
 */
export async function promptFindTeuTrackingOnSave({ modelName, itemId, t }) {
  const mn = String(modelName || "").trim();
  const id = itemId != null ? String(itemId).trim() : "";
  if (!mn || !id || typeof t !== "function") return;

  let status;
  try {
    const rule = await findTeuService.getCachedAutoTrackingRuleForModel(mn);
    if (!rule || getRuleOnSaveMode(rule) !== "ask") return;
    status = await findTeuService.getAutoTrackingItemStatus(mn, id);
  } catch (_) {
    return;
  }
  if (!status || !status.hasRule || status.onSaveMode !== "ask") return;
  if (!Array.isArray(status.candidates) || status.candidates.length === 0) return;
  if (status.activeTracking) return;
  // Se desactivó solo porque el embarque terminó: no se pregunta en cada guardado.
  if (status.autoStoppedTracking) return;

  const first = status.candidates[0];
  const choice = await Swal.fire({
    icon: "question",
    title: t("findTeu.askOnSaveTitle"),
    html: t("findTeu.askOnSaveText", {
      number: `<strong>${escapeHtml(first.trackingNumber)}</strong>`,
      type: escapeHtml(String(first.trackingType || "").toUpperCase()),
    }),
    showCancelButton: true,
    confirmButtonText: t("findTeu.askOnSaveConfirm"),
    cancelButtonText: t("findTeu.askOnSaveCancel"),
    reverseButtons: true,
    focusCancel: false,
  });
  if (!choice.isConfirmed) return;

  try {
    Swal.fire({
      title: t("findTeu.askOnSaveStarting"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    const res = await findTeuService.startAutoTrackingForItem(mn, id);
    if (res?.started || res?.status === "started") {
      await Swal.fire({
        icon: "success",
        title: t("findTeu.trackingStartedTitle"),
        text: t("findTeu.trackingStartedSuccess"),
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }
    const hints = Array.isArray(res?.hints) ? res.hints.filter(Boolean) : [];
    const hintsHtml = hints.length
      ? `<p class="fw-semibold small mt-3 mb-1">${escapeHtml(t("findTeu.hintsTitle"))}</p>` +
        `<ol class="small mb-0 ps-3">${hints
          .map((code) => `<li>${escapeHtml(t(`findTeu.hint_${code}`))}</li>`)
          .join("")}</ol>`
      : "";
    await Swal.fire({
      icon: hintsHtml ? "error" : "info",
      title: t("findTeu.errorStartingTracking"),
      html: `<div class="text-start">${escapeHtml(
        res?.message || res?.error || t("findTeu.startFromItemSkipped")
      )}${hintsHtml}</div>`,
    });
  } catch (e) {
    await Swal.fire({
      icon: "error",
      title: t("findTeu.errorStartingTracking"),
      text: e.response?.data?.error || e.message || "Error",
    });
  }
}
